import {
  Box,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { memo } from "react";

import { sectionInnerSx } from "./sectionStyles";
import type { SendMode } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import type { ReactNode } from "react";

import { isCampaignLocked } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import { InfoOutlinedIcon } from "@/icons";
import OrionSwitch from "@/orion/OrionComponents/OrionSwitch";
// Shared dayjs instance with the utc + timezone plugins already extended.
import dayjs from "@/utils/configuredDayjs";
import { dayjsToIsoOrNull } from "@/utils/dateUtils";

export interface BroadcastScheduleSectionProps {
  sendMode: SendMode;
  onSendModeChange: (mode: SendMode) => void;
  scheduledAtIso: string | null;
  onScheduledAtChange: (iso: string | null) => void;
  scheduleError?: string;
  disabled?: boolean;
  /**
   * IANA timezone the picker should display/interpret in (the store's
   * timezone). When omitted, the picker falls back to the browser's local zone.
   */
  timezone?: string;
  caption?: ReactNode;
  /** Optional notice rendered above the picker (e.g. a quiet-hours warning). */
  notice?: ReactNode;
  /** Optional notice rendered below the "Send instantly" option (SMS quiet hours). */
  instantNotice?: ReactNode;
  /** Campaign date span ("YYYY-MM-DD") when composing from the Marketing
   * Calendar plan wizard: disables Send Instantly and clamps the picker to
   * the range (in `timezone` when given, else the browser's local zone). */
  scheduleWindow?: { startDate: string; endDate: string } | null;
  /** Broadcast carries the marketing_calendar stamp: disables Send Instantly
   * even when the owning campaign's window is unknown (no picker clamp). */
  calendarOwned?: boolean;
  /** Whether to show the Smart Send toggle (feature-flagged). */
  showSmartSend?: boolean;
  /** Smart Send on/off. Independent of send mode and the schedule picker. */
  smartSend: boolean;
  onSmartSendChange: (value: boolean) => void;
  smartSendDisabledReason?: string;
}

/** Smart Send explainer copy, surfaced via the info-icon hover tooltip. */
const SMART_SEND_DESCRIPTION =
  "Optimizes delivery for each recipient's best engagement time after your send begins — works with both immediate and scheduled sends.";

/** Why a control is locked, on hover — replaces the unlink banner
 * (FOG-2496). Per tooltip guidance: why it is unavailable, then the way
 * forward, in plain full sentences. */
const CALENDAR_LOCK_TOOLTIP =
  "This broadcast's schedule is managed by its Marketing Calendar campaign.";
const CALENDAR_LOCK_INSTANT_TOOLTIP =
  "Broadcasts in a Marketing Calendar campaign can't send instantly. Use Schedule to pick a time within the campaign dates.";

interface ScheduleOptionProps {
  value: SendMode;
  selected: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  icon?: ReactNode;
  /** Hover tooltip naming why the option is locked; shown only while disabled. */
  disabledReason?: string;
}

function ScheduleOption({
  value,
  selected,
  disabled,
  title,
  description,
  icon,
  disabledReason,
}: ScheduleOptionProps) {
  const option = (
    <Box
      component="label"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 4,
        py: 3,
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        border: `1.5px solid ${
          selected ? theme.palette.primary.main : theme.palette.divider
        }`,
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.06)
          : "transparent",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: theme.transitions.create(
          ["border-color", "background-color"],
          { duration: theme.transitions.duration.short },
        ),
      })}
    >
      <Radio value={value} disabled={disabled} size="small" sx={{ p: 0 }} />
      <Stack gap="2px">
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {icon}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </Box>
  );
  if (!disabled || !disabledReason) return option;
  return (
    // describeChild: the default aria-label mode would override the label
    // text as the radio's accessible name.
    <Tooltip title={disabledReason} arrow describeChild>
      {option}
    </Tooltip>
  );
}

/** Send-mode radio group + schedule picker. Controlled; shared across channels. */
export const BroadcastScheduleSection = memo(function BroadcastScheduleSection({
  sendMode,
  onSendModeChange,
  scheduledAtIso,
  onScheduledAtChange,
  scheduleError,
  disabled,
  timezone,
  caption,
  notice,
  instantNotice,
  scheduleWindow,
  calendarOwned,
  showSmartSend,
  smartSend,
  onSmartSendChange,
  smartSendDisabledReason,
}: BroadcastScheduleSectionProps) {
  // Human-readable label for the configured timezone, e.g. "Eastern Standard
  // Time". Null when no store timezone is supplied (picker stays browser-local).
  const timezoneLabel = timezone
    ? dayjs().tz(timezone).offsetName("long")
    : null;
  // Campaign span → picker bounds, interpreted in the same zone the picker
  // displays in. disablePast still applies, so the effective minimum is the
  // later of "now" and the campaign start.
  const toWindowDay = (date: string) =>
    timezone ? dayjs.tz(date, timezone) : dayjs(date);
  const windowMin = scheduleWindow
    ? toWindowDay(scheduleWindow.startDate).startOf("day")
    : undefined;
  const windowMax = scheduleWindow
    ? toWindowDay(scheduleWindow.endDate).endOf("day")
    : undefined;
  const campaignLocked = isCampaignLocked(calendarOwned, scheduleWindow);
  // A stamped broadcast with no resolved campaign window (still loading, or a
  // stale stamp) must not offer an unclamped picker the copy claims is clamped.
  const scheduleLocked = Boolean(calendarOwned && !scheduleWindow);
  return (
    <Stack
      gap={2}
      sx={{
        backgroundColor: (theme) =>
          theme.palette.background["paper-elevation-0"],
        borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
        p: 6,
      }}
    >
      <Typography variant="subtitle1">Scheduling</Typography>
      {caption}
      {showSmartSend && (
        <Stack direction="row" alignItems="center" gap={3}>
          <Tooltip title={smartSendDisabledReason ?? ""} arrow>
            <Box component="span" sx={{ display: "inline-flex" }}>
              <OrionSwitch
                checked={smartSend}
                disabled={disabled || Boolean(smartSendDisabledReason)}
                onChange={(_event, next) => onSmartSendChange(next)}
                inputProps={{ "aria-label": "Enable Smart Send" }}
              />
            </Box>
          </Tooltip>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Smart Send
          </Typography>
          <Tooltip title={SMART_SEND_DESCRIPTION} arrow>
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center" }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "text.secondary",
                  cursor: "pointer",
                }}
              />
            </Box>
          </Tooltip>
        </Stack>
      )}
      <Stack gap={4} sx={sectionInnerSx}>
        <RadioGroup
          value={sendMode}
          onChange={(e) => onSendModeChange(e.target.value as SendMode)}
          sx={{ gap: 4 }}
        >
          <ScheduleOption
            value="instant"
            selected={sendMode === "instant"}
            disabled={disabled || campaignLocked}
            disabledReason={
              campaignLocked ? CALENDAR_LOCK_INSTANT_TOOLTIP : undefined
            }
            title="Send instantly"
            description={
              campaignLocked
                ? "Campaign broadcasts are scheduled within the campaign dates"
                : smartSend
                  ? "Smart Send begins delivering as soon as broadcast is confirmed"
                  : "Send to all recipients as soon as broadcast is confirmed"
            }
          />
          {instantNotice}
          <ScheduleOption
            value="schedule"
            selected={sendMode === "schedule"}
            disabled={disabled || scheduleLocked}
            disabledReason={scheduleLocked ? CALENDAR_LOCK_TOOLTIP : undefined}
            title="Schedule"
            description={
              scheduleLocked
                ? "Campaign dates unavailable. Check the campaign in the Marketing Calendar"
                : smartSend
                  ? "Smart Send begins delivering at your scheduled time"
                  : "Choose a specific date and time to send"
            }
          />
        </RadioGroup>

        {sendMode === "schedule" && (
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              Schedule date and time
            </Typography>
            {notice}
            {/* Span wrapper: a disabled picker swallows hover events. */}
            <Tooltip
              title={scheduleLocked ? CALENDAR_LOCK_TOOLTIP : ""}
              arrow
              describeChild
            >
              <Box component="span" sx={{ display: "block" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    // Display/interpret the picker in the store's timezone so the
                    // wall-clock time the user picks matches the caption below.
                    // `toISOString()` always serializes to UTC for the wire.
                    {...(timezone ? { timezone } : {})}
                    value={
                      scheduledAtIso
                        ? timezone
                          ? dayjs(scheduledAtIso).tz(timezone)
                          : dayjs(scheduledAtIso)
                        : null
                    }
                    onChange={(next) =>
                      onScheduledAtChange(dayjsToIsoOrNull(next))
                    }
                    disabled={disabled || scheduleLocked}
                    disablePast
                    {...(windowMin ? { minDateTime: windowMin } : {})}
                    {...(windowMax ? { maxDateTime: windowMax } : {})}
                    // No minutesStep: it would flag typed off-interval times
                    // (e.g. 3:07) as invalid. The 15-min steps below only drive the
                    // scroll picker, not typed input.
                    timeSteps={{ minutes: 15 }}
                    // Keep the single scrolling column: 1440/15 = 96 options must be
                    // <= threshold, else it splits into hour/minute/AM-PM columns.
                    thresholdToRenderTimeInASingleColumn={96}
                    slotProps={{
                      textField: {
                        inputProps: { "aria-label": "Schedule date and time" },
                        fullWidth: true,
                        size: "small",
                        sx: (theme) => ({
                          backgroundColor: theme.palette.common.white,
                        }),
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Tooltip>
            {scheduleError && (
              <Typography variant="caption" color="error">
                {scheduleError}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              fontStyle="italic"
            >
              {timezone
                ? timezoneLabel
                  ? `In your store's timezone (${timezoneLabel})`
                  : "In your store's timezone settings"
                : "In your local timezone"}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});
